import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { SectionComponentType } from "@/types/property-panel.types";
import { useDraggable } from "@dnd-kit/core";

import { cn } from "@/lib/utils";

interface DraggableSectionProps {
  section: SectionComponentType;
}

/** Convert JSX className= to HTML class= for raw rendering */
function jsxToHtml(jsx: string): string {
  return jsx.replace(/className=/g, "class=");
}

/** Scale factor for preview container */
const PREVIEW_SCALE = 0.28;
/** Width of the "virtual" full-size render (px) */
const PREVIEW_INNER_W = 1280;

function DraggableSection({ section }: DraggableSectionProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `drag-${section.componentType}`,
    data: {
      elementType: section.componentType,
      isSection: true,
      content: section.content,
    },
  });

  const Icon = section.icon;
  const [hovered, setHovered] = useState(false);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  const isNav = section.category === "navigation";
  const previewInnerH = isNav ? 200 : 600;
  const scale = isNav ? 0.6 : PREVIEW_SCALE;
  const scaledW = PREVIEW_INNER_W * scale;
  const scaledH = previewInnerH * scale;

  const showPreview = useCallback(() => {
    hoverTimeout.current = setTimeout(() => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      const popupH = scaledH + 32; // scaled height + label bar
      // Vertical: center on button, clamp to viewport
      let top = rect.top + rect.height / 2 - popupH / 2;
      top = Math.max(8, Math.min(top, window.innerHeight - popupH - 8));
      setPopupPos({ top, left: rect.right + 8 });
      setHovered(true);
    }, 300);
  }, [scaledH]);

  const hidePreview = useCallback(() => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setHovered(false);
  }, []);

  // Content is from our own hardcoded template constants (helper.ts), not user input
  const previewHtml = section.content ? jsxToHtml(section.content) : "";

  return (
    <>
      <button
        ref={(node) => {
          setNodeRef(node);
          btnRef.current = node;
        }}
        {...listeners}
        {...attributes}
        className={cn(
          "flex flex-col items-center justify-center gap-1.5 w-full px-2 py-3",
          "bg-muted/50 rounded-md border border-border text-foreground",
          "hover:bg-accent hover:border-primary/50 transition-all text-xs",
          "cursor-grab active:cursor-grabbing",
          isDragging && "opacity-50",
        )}
        title={`Drag to add ${section.name}`}
        onMouseEnter={showPreview}
        onMouseLeave={hidePreview}
        onMouseDown={hidePreview}
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 shrink-0 pointer-events-none">
          <Icon className="w-4 h-4 text-primary pointer-events-none" />
        </div>
        <span className="text-[11px] font-medium text-muted-foreground pointer-events-none">
          {section.name}
        </span>
      </button>

      {/* Hover preview — portaled to body to avoid ScrollArea clipping */}
      {hovered &&
        previewHtml &&
        createPortal(
          <div
            className="fixed z-[9999] pointer-events-none animate-in fade-in-0 zoom-in-95 duration-150"
            style={{ top: popupPos.top, left: popupPos.left }}
          >
            <div className="rounded-lg border border-border bg-background shadow-xl overflow-hidden">

              {/* Scaled preview */}
              <div
                style={{
                  width: scaledW,
                  height: scaledH,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: PREVIEW_INNER_W,
                    height: previewInnerH,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    overflow: "hidden",
                  }}
                  className="bg-white dark:bg-gray-950"
                  // Safe: content is from hardcoded template constants, not user input
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export default DraggableSection;
